import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ZoomInIcon } from 'lucide-react';
import { insidePages } from '../data/content';
import { PageLightbox } from './PageLightbox';

/** Real product pages — swipe on touch, arrows and dots on desktop, tap to zoom. */
export function InsidePagesCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState<number | null>(null);
  const total = insidePages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (slide) track.scrollTo({
      left: slide.offsetLeft,
      behavior: 'smooth'
    });
  }, [total]);
  const closeZoom = useCallback(() => setZoomed(null), []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const slides = Array.from(track.children) as HTMLElement[];
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      if (atEnd) {
        setActive(total - 1);
        return;
      }
      let closest = 0;
      slides.forEach((slide, index) => {
        if (Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[closest].offsetLeft - track.scrollLeft)) {
          closest = index;
        }
      });
      setActive(closest);
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, [total]);
  return <div className="relative mx-auto max-w-5xl" aria-roledescription="carousel" aria-label="Pagine di Lash & Brow Instructor Ready" data-id="element-17" data-mp="3trkjmrvuu11g">
      <div ref={trackRef} className="no-scrollbar relative flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2" data-id="element-18" data-mp="18y7bngtmbuuk">
        {insidePages.map((page, index) => <figure key={page.src} className="w-[82%] flex-none snap-start sm:w-[calc((100%-1rem)/2)] lg:w-[calc((100%-2rem)/3)]" aria-roledescription="slide" aria-label={`${index + 1} di ${total}: ${page.caption}`} data-id="element-19" data-mp="3a1ghmgy2svs6" data-mpi={index}>
            <button type="button" onClick={() => setZoomed(index)} aria-label={`Ingrandisci: ${page.caption}`} className="group relative block w-full rounded-[18px] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta focus-visible:ring-offset-2" data-id="element-20" data-mp="3j028r0hm5j4k">
              <img src={page.src} alt={`${page.caption} — pagina reale di Lash & Brow Instructor Ready`} width={960} height={1200} loading={index < 2 ? 'eager' : 'lazy'} decoding="async" draggable={false} className="block aspect-[4/5] h-auto w-full select-none rounded-[18px] border border-brand-charcoal/10 bg-white object-contain shadow-[0_30px_60px_-36px_rgba(120,70,80,0.45)]" data-id="element-21" data-mp="3030tmmuci2mw" />
              <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-brand-charcoal shadow-sm transition-transform duration-150 ease-out group-hover:scale-105" data-id="element-22" data-mp="mxm9d0cvoiep">
                <ZoomInIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-23" data-mp="e4se3th1d8f7" />
              </span>
            </button>
          </figure>)}
      </div>

      <ArrowButton side="left" label="Pagina precedente" onClick={() => goTo(active - 1)} data-id="element-24" data-mp="15vq9j5asf74d">
        <ChevronLeftIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-25" data-mp="rw72ygxmk3u6" />
      </ArrowButton>
      <ArrowButton side="right" label="Pagina successiva" onClick={() => goTo(active + 1)} data-id="element-26" data-mp="wtz481k5t2b1">
        <ChevronRightIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-27" data-mp="3cld6hw36nzht" />
      </ArrowButton>

      <div className="mt-6 flex flex-col items-center gap-3" data-id="element-28" data-mp="3hl9a3u7yxowu">
        <p className="text-center font-body text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-charcoal/55" aria-live="polite" data-id="element-29" data-mp="3himvjamnu954">
          {insidePages[active].caption}
        </p>
        <div className="flex items-center gap-2.5" data-id="element-30" data-mp="3o1sxllyaqiuj">
          {insidePages.map((page, index) => <button key={page.src} type="button" aria-label={`Mostra ${page.caption}`} aria-current={index === active} onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta focus-visible:ring-offset-2 ${index === active ? 'w-8 bg-brand-cta' : 'w-2 bg-brand-charcoal/20 hover:bg-brand-charcoal/35'}`} data-id="element-31" data-mp="3j3rej10vejj2" data-mpi={index} />)}
        </div>
      </div>

      <PageLightbox page={zoomed === null ? null : insidePages[zoomed]} onClose={closeZoom} data-id="element-32" data-mp="2qe10erz8nhmy" />
    </div>;
}
function ArrowButton({
  side,
  label,
  onClick,
  children
}: {
  side: 'left' | 'right';
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} aria-label={label} className={`absolute top-[calc(50%-2.5rem)] hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-brand-charcoal/10 bg-white text-brand-charcoal shadow-[0_10px_24px_-12px_rgba(32,33,38,0.4)] transition-[background-color,transform] duration-150 ease-out hover:bg-brand-paper active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta md:flex ${side === 'left' ? '-left-6' : '-right-6'}`} data-id="element-33" data-mp="2q8t8a9q16ix2">
      {children}
    </button>;
}